export class FikoIfadeHata extends Error {
  constructor(m: string) { super(m); this.name = 'FikoIfadeHata'; }
}

export type ParamCozumleyici = (ad: string) => number | boolean | string | undefined;
export interface IfadeSonuc { deger: number | boolean | string; referanslar: string[]; }

type Tok = { t: 'num' | 'id' | 'op' | 'lp' | 'rp' | 'comma'; v: string; n?: number };

const SABITLER: Record<string, number> = { PI: Math.PI, E: Math.E };
const BIRIM: Record<string, number> = { mm: 1, cm: 10, m: 1000, deg: 1, '°': 1, rad: 180 / Math.PI };
const D2R = Math.PI / 180, R2D = 180 / Math.PI;
const FONK: Record<string, (a: number[]) => number> = {
  sin: a => Math.sin(a[0] * D2R), cos: a => Math.cos(a[0] * D2R), tan: a => Math.tan(a[0] * D2R),
  asin: a => Math.asin(a[0]) * R2D, acos: a => Math.acos(a[0]) * R2D, atan: a => Math.atan(a[0]) * R2D,
  atan2: a => Math.atan2(a[0], a[1]) * R2D,
  sqrt: a => Math.sqrt(a[0]), abs: a => Math.abs(a[0]), sign: a => Math.sign(a[0]),
  min: a => Math.min(...a), max: a => Math.max(...a), pow: a => Math.pow(a[0], a[1]),
  round: a => Math.round(a[0]), floor: a => Math.floor(a[0]), ceil: a => Math.ceil(a[0]),
  log: a => Math.log(a[0]), exp: a => Math.exp(a[0]),
};

function tokenize(s: string): Tok[] {
  const out: Tok[] = []; let i = 0;
  const ops = ['<=', '>=', '==', '!=', '&&', '||', '+', '-', '*', '/', '%', '^', '<', '>', '!'];
  while (i < s.length) {
    const c = s[i];
    if (c === ' ' || c === '\t' || c === '\n') { i++; continue; }
    if (c === '(') { out.push({ t: 'lp', v: c }); i++; continue; }
    if (c === ')') { out.push({ t: 'rp', v: c }); i++; continue; }
    if (c === ',') { out.push({ t: 'comma', v: c }); i++; continue; }
    if (c === '`') {   // CATIA tarzı tırnaklı tanımlayıcı: `Primitif.1 - Kutu\En` gibi özel karakterli ad
      let j = i + 1; while (j < s.length && s[j] !== '`') j++;
      if (j >= s.length) throw new FikoIfadeHata('Kapanmamış ` (backtick)');
      out.push({ t: 'id', v: s.slice(i + 1, j) }); i = j + 1; continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i; while (j < s.length && /[0-9.]/.test(s[j])) j++;
      let num = parseFloat(s.slice(i, j));
      const rest = s.slice(j);
      const bm = rest.match(/^(mm|cm|m|deg|rad|°)/);
      if (bm) { num *= BIRIM[bm[1]]; j += bm[1].length; }
      out.push({ t: 'num', v: s.slice(i, j), n: num }); i = j; continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i; while (j < s.length && /[A-Za-z0-9_]/.test(s[j])) j++;
      out.push({ t: 'id', v: s.slice(i, j) }); i = j; continue;
    }
    const op = ops.find(o => s.startsWith(o, i));
    if (op) { out.push({ t: 'op', v: op }); i += op.length; continue; }
    throw new FikoIfadeHata(`Geçersiz karakter: "${c}"`);
  }
  return out;
}

type Node =
  | { k: 'num'; v: number }
  | { k: 'id'; v: string }
  | { k: 'un'; op: string; a: Node }
  | { k: 'bin'; op: string; a: Node; b: Node }
  | { k: 'call'; ad: string; args: Node[] };

const ONCELIK: Record<string, number> = { '||': 1, '&&': 2, '==': 3, '!=': 3, '<': 4, '<=': 4, '>': 4, '>=': 4, '+': 5, '-': 5, '*': 6, '/': 6, '%': 6, '^': 8 };

class Parser {
  i = 0;
  constructor(private toks: Tok[]) {}
  peek(): Tok | undefined { return this.toks[this.i]; }
  next(): Tok { const t = this.toks[this.i++]; if (!t) throw new FikoIfadeHata('Beklenmeyen ifade sonu'); return t; }
  parse(): Node { const n = this.expr(0); if (this.i < this.toks.length) throw new FikoIfadeHata('Fazla simge'); return n; }
  expr(min: number): Node {
    let sol = this.atom();
    while (true) {
      const t = this.peek();
      if (!t || t.t !== 'op' || ONCELIK[t.v] == null || ONCELIK[t.v] < min) break;
      const op = t.v; const sag = ONCELIK[op]; this.i++;
      const sonraki = op === '^' ? sag : sag + 1;
      const b = this.expr(sonraki);
      sol = { k: 'bin', op, a: sol, b };
    }
    return sol;
  }
  atom(): Node {
    const t = this.next();
    if (t.t === 'op' && (t.v === '-' || t.v === '!')) return { k: 'un', op: t.v, a: this.expr(7) };
    if (t.t === 'num') return { k: 'num', v: t.n! };
    if (t.t === 'lp') { const n = this.expr(0); const r = this.next(); if (r.t !== 'rp') throw new FikoIfadeHata('")" bekleniyordu'); return n; }
    if (t.t === 'id') {
      if (this.peek()?.t === 'lp') {
        this.i++; const args: Node[] = [];
        if (this.peek()?.t !== 'rp') { args.push(this.expr(0)); while (this.peek()?.t === 'comma') { this.i++; args.push(this.expr(0)); } }
        const r = this.next(); if (r.t !== 'rp') throw new FikoIfadeHata('")" bekleniyordu');
        return { k: 'call', ad: t.v, args };
      }
      return { k: 'id', v: t.v };
    }
    throw new FikoIfadeHata(`Beklenmeyen simge: "${t.v}"`);
  }
}

function topla(n: Node, refs: Set<string>): void {
  if (n.k === 'id') { if (SABITLER[n.v] == null) refs.add(n.v); }
  else if (n.k === 'un') topla(n.a, refs);
  else if (n.k === 'bin') { topla(n.a, refs); topla(n.b, refs); }
  else if (n.k === 'call') n.args.forEach(a => topla(a, refs));
}

function num(v: number | boolean | string): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const f = parseFloat(v as string); return isNaN(f) ? 0 : f;
}

function eval2(n: Node, coz: ParamCozumleyici): number {
  switch (n.k) {
    case 'num': return n.v;
    case 'id': {
      if (SABITLER[n.v] != null) return SABITLER[n.v];
      const d = coz(n.v);
      if (d === undefined) throw new FikoIfadeHata(`Bilinmeyen parametre: "${n.v}"`);
      return num(d);
    }
    case 'un': { const a = eval2(n.a, coz); return n.op === '-' ? -a : (a ? 0 : 1); }
    case 'call': {
      if (n.ad === 'if') { if (n.args.length !== 3) throw new FikoIfadeHata('if(kosul,a,b) 3 argüman ister'); return eval2(n.args[0], coz) ? eval2(n.args[1], coz) : eval2(n.args[2], coz); }
      const f = FONK[n.ad]; if (!f) throw new FikoIfadeHata(`Bilinmeyen fonksiyon: "${n.ad}"`);
      return f(n.args.map(a => eval2(a, coz)));
    }
    case 'bin': {
      const a = eval2(n.a, coz), b = eval2(n.b, coz);
      switch (n.op) {
        case '+': return a + b; case '-': return a - b; case '*': return a * b;
        case '/': if (Math.abs(b) < 1e-12) throw new FikoIfadeHata('Sıfıra bölme'); return a / b;
        case '%': if (Math.abs(b) < 1e-12) throw new FikoIfadeHata('Sıfırla mod alma'); return a % b;
        case '^': return Math.pow(a, b);
        case '<': return a < b ? 1 : 0; case '<=': return a <= b ? 1 : 0;
        case '>': return a > b ? 1 : 0; case '>=': return a >= b ? 1 : 0;
        case '==': return a === b ? 1 : 0; case '!=': return a !== b ? 1 : 0;
        case '&&': return a && b ? 1 : 0; case '||': return a || b ? 1 : 0;
      }
      throw new FikoIfadeHata(`Bilinmeyen operatör: "${n.op}"`);
    }
  }
}

export class FikoIfadeMotoru {
  static degerlendir(ifade: string, coz: ParamCozumleyici): IfadeSonuc {
    if (!ifade || !ifade.trim()) throw new FikoIfadeHata('Boş ifade');
    const ast = new Parser(tokenize(ifade)).parse();
    const refs = new Set<string>(); topla(ast, refs);
    return { deger: eval2(ast, coz), referanslar: [...refs] };
  }
  static referanslar(ifade: string): string[] {
    try { const ast = new Parser(tokenize(ifade)).parse(); const r = new Set<string>(); topla(ast, r); return [...r]; }
    catch { return []; }
  }
}
